import stateFees from '../StateFees/prices.json';
import { formatCurrency } from '../../../utilities/financing';
import { generateUUID } from '../../../utilities/generateUUID';
import { resolveService } from '../../../utilities/service-resolver';

const safeString = (str) => {
  const textArea = document.createElement('textarea');
  textArea.innerText = str;
  return textArea.innerHTML.split('<br>').join('') || str;
};

// function generating product list before checkout
export const generateProductsList = (installOption, addons, qty) => {
  const products = [
    {
      id: installOption.id,
      type: 'INSTALL',
      name: installOption.name === 'TDSTANDARD' ? 'TD Standard Installation' : 'Trailer Tire Installation',
      description: safeString(installOption.description),
      subs: installOption.subs.map((sub) => ({ name: safeString(sub) })),
      unitPrice: installOption.unitPrice,
      qty,
      position: 0,
      checked: true,
    },
  ];

  Object.keys(addons).forEach((key) => {
    addons[key]
      .filter((addon) => addon.checked)
      .forEach((addon) => {
        products.push({
          id: addon.id,
          type: 'ADDON',
          name: addon.name,
          description: addon.description,
          subs: addon.subs.map((sub) => ({ name: safeString(sub) })),
          unitPrice: addon.unitPrice,
          qty: addon.qty,
          position: 0,
          checked: true,
        });
      });
  });

  return products;
};

const getMinQuantity = (deal) => {
  let minQuantity = deal.discount.minQuantity ? Number.parseInt(deal.discount.minQuantity, 10) : 1;

  // default to 1 for the sake of calculations
  if (minQuantity === 0) {
    minQuantity = 1;
  }

  return minQuantity;
};

// function checking if vehicle qualifies for free alignment
export const isFreeAlignmentEligible = ({
  isTrailerTire,
  installOption,
  quantities,
  isSplitFitment,
  product,
}) => {
  // trailer tires installations are not eligible for free alignment
  // TODO: fix this hacky conditional @see InstallationOption component
  if (isTrailerTire || ((installOption.name || installOption.key) !== 'TDSTANDARD')) {
    return false;
  }

  if (typeof product.deals === 'undefined' || product?.deals?.length === 0) {
    return false;
  }

  const { deals } = product;

  const freeAlignmentDeals = deals.filter((deal) => deal.discount.isFreeAlignment);
  let isFreeAlignment = false;
  if (freeAlignmentDeals.length > 0) {
    freeAlignmentDeals.forEach((deal) => {
      const minQuantity = getMinQuantity(deal);

      let qty = quantities.qtyFront;
      if (isSplitFitment) {
        qty += quantities.qtyRear;
      }

      if (qty >= minQuantity) {
        isFreeAlignment = true;
      }
    });
  }

  // console.log({ isFreeAlignment });
  return isFreeAlignment;
};

const getMaxQuantity = (deal) => {
  // default to 0 if not set, which will be treated as unlimited
  const result = deal.discount.maxQuantity ? Number.parseFloat(deal.discount.maxQuantity, 10) : 0;
  return result;
};

const getMaxAmount = (deal) => {
  // default to 0 if not set, which will be treated as unlimited
  const result = deal.discount.maxAmount ? Number.parseFloat(deal.discount.maxAmount) : 0;
  return result;
};

const getMaxConditional = (deal, maximumQuantity, maximumAmount) => {
  // default to AND if not set
  let maxConditional = deal.discount.maxConditional ? deal.discount.maxConditional : 'AND';

  // if maxQuantity or maxAmount are 0 (which is default if not set),
  // then make sure maxConditional is set to 'AND'
  if (maxConditional === 'OR' && (maximumQuantity === 0 || maximumAmount === 0)) {
    maxConditional = 'AND';
  }

  return maxConditional;
};

const meetsMaximumAmountThreshold = (
  maximumAmount,
  priceBeforeDeals,
) => {
  // treat unset maximumAmount and maximumAmount = 0 as unlimited
  if (!maximumAmount || maximumAmount === 0) {
    return true;
  }

  return (priceBeforeDeals <= maximumAmount);
};

const meetsMaximumQuantityThreshold = (
  maximumQuantity,
  maximumAmount,
  numberOfTires,
) => {
  // treat unset maximumQuantity and maximumQuantity = 0 as unlimited
  if (!maximumQuantity || maximumQuantity === 0) {
    return true;
  }

  return (numberOfTires <= maximumAmount);
};

const isDealValid = (
  deal,
  numberOfTires,
  priceBeforeDeals,
) => {
  let addDeal = true;

  const minimumQuantity = getMinQuantity(deal);
  const minimumAmount = deal.discount.minAmount ? Number.parseFloat(deal.discount.minAmount) : 0;

  const maximumQuantity = getMaxQuantity(deal);
  const maximumAmount = getMaxAmount(deal);

  // boolean will return true if condition is valid
  const meetsMinAmountThreshold = priceBeforeDeals != null && priceBeforeDeals >= minimumAmount;
  const meetsMinQuantityThreshold = numberOfTires >= minimumQuantity;

  const meetsMaxAmountThreshold = meetsMaximumAmountThreshold(maximumAmount, priceBeforeDeals);

  // TODO: maximum quantity threshold does not work the same as minimum quantity - so we want
  //  to handle this calculation differently
  // Rather than check if the amount selected meets the max quantity threshold, instead we can
  //  use the maxQuantity value to determine the maximum number of times the deal should be applied
  const meetsMaxQuantityThreshold = meetsMaximumQuantityThreshold(
    maximumQuantity,
    maximumAmount,
    numberOfTires,
  );

  const maxConditional = getMaxConditional(deal, maximumQuantity, maximumAmount);

  // console.log(deal, addDeal, priceBeforeDeals, numberOfTires);
  // console.log(`maximumAmount: ${maximumAmount}, maximumQuantity: ${maximumQuantity}`);
  // console.log(maxConditional: ${maxConditional}`);
  // console.log(`minimumAmount: ${minimumAmount}, minimumQuantity: ${minimumQuantity}`);
  // console.log(`meetsMinAmountThreshold: ${meetsMinAmountThreshold}`);
  // console.log(`meetsMinQuantityThreshold: ${meetsMinQuantityThreshold}`);
  // console.log(`meetsMaxAmountThreshold: ${meetsMaxAmountThreshold}`);
  // console.log(`meetsMaxQuantityThreshold: ${meetsMaxQuantityThreshold}`);
  // console.log('numbertires', numberOfTires);

  // remove deal and do not sum if it does not meet min/max criteria
  if (deal.discount.minConditional === 'AND') {
    if (!(meetsMinAmountThreshold && meetsMinQuantityThreshold)) {
      // console.log(deal, 'failed 1.0');
      addDeal = false;
    }
  }

  if (deal.discount.minConditional === 'OR') {
    if (!(meetsMinAmountThreshold || meetsMinQuantityThreshold)) {
      // console.log(deal, 'failed 1.1');
      addDeal = false;
    }
  }

  if (maxConditional === 'AND') {
    if (!(meetsMaxAmountThreshold && meetsMaxQuantityThreshold)) {
      // console.log(deal, 'failed 1.2');
      addDeal = false;
    }
  }

  if (maxConditional === 'OR') {
    if (!(meetsMaxAmountThreshold || meetsMaxQuantityThreshold)) {
      // console.log(deal, 'failed 1.3');
      addDeal = false;
    }
  }

  // console.log(`addDeal ${addDeal}`);
  return addDeal;
};

const calculateTotalPercentDiscount = (
  numberOfTires,
  priceBeforeDeals,
  discountAmount,
  minimumQuantity,
) => {
  let totalDiscountValue = 0;

  // TODO: make sure there's no javascript math weirdness to handle
  const discountPercent = discountAmount / 100;

  // get price per tire
  const pricePerTire = (priceBeforeDeals / numberOfTires);

  // multiply pricePerTire by minimum quantity to calculate the discount value
  const percentDiscountValue = (pricePerTire * minimumQuantity) * discountPercent;

  const numberOfTimesToApplyDiscount = Math.floor(numberOfTires / minimumQuantity);

  // calculate percent Discount Value times number of times to apply discount
  totalDiscountValue = percentDiscountValue * numberOfTimesToApplyDiscount;
  // console.log(`totalDiscountValue: ${totalDiscountValue}`);

  return totalDiscountValue;
};

const calculateTotalDiscountValue = (
  deal,
  discountAmount,
  numberOfTires,
  priceBeforeDeals,
) => {
  let totalDiscountValue = 0;

  if (discountAmount > 0) {
    const minimumQuantity = getMinQuantity(deal);
    const { amountType } = deal.discount;

    if (amountType === 'fixed') {
      totalDiscountValue = discountAmount * (Math.floor(numberOfTires / minimumQuantity));
    }

    if (amountType === 'percent') {
      totalDiscountValue = calculateTotalPercentDiscount(
        numberOfTires,
        priceBeforeDeals,
        discountAmount,
        minimumQuantity,
      );
    }
  }

  return totalDiscountValue;
};

// function checking if customer selection meets rebate standards based off of amount and quality
export const getDiscountValue = (
  deal,
  numberOfTires,
  priceBeforeDeals,
) => {
  let totalDiscountValue = 0;

  if (!deal.discount) {
    return 0;
  }

  const discountAmount = Number.parseFloat(deal.discount.amount);
  // console.log(`discountAmount: ${discountAmount}`);

  if (Number.isNaN(discountAmount)) {
    return 0;
  }

  const addDeal = isDealValid(deal, numberOfTires, priceBeforeDeals);
  // console.log(`addDeal: ${addDeal}`);

  if (addDeal) {
    totalDiscountValue = calculateTotalDiscountValue(
      deal,
      discountAmount,
      numberOfTires,
      priceBeforeDeals,
    );
  }

  return totalDiscountValue;
};

export const getEligibleDeals = (
  deals,
  type,
  numberOfTires,
  priceBeforeDeals = null,
) => {
  if (typeof deals === 'undefined') return [];

  // if type is null dont filter deals by type
  if (type !== null) {
    // only calculate deals that match the given discount type
    deals = deals.filter((deal) => deal.discount && deal.discount.type === type);
  }

  const validDeals = [];
  deals.forEach((deal) => {
    const isValid = isDealValid(deal, numberOfTires, priceBeforeDeals);
    if (isValid) {
      validDeals.push(deal);
    }
  });

  return validDeals;
};

export const getTotalDealValue = (
  deals,
  type,
  numberOfTires,
  priceBeforeDeals = null,
  // priceForTiresOnly = 0,
) => {
  let totalDealValue = 0;

  if (typeof deals === 'undefined') return totalDealValue;

  // only calculate deals that match the given discount type
  deals = deals.filter((deal) => deal.discount && deal.discount.type === type);

  deals.forEach((deal) => {
    const isValid = isDealValid(deal, numberOfTires, priceBeforeDeals);
    if (isValid) {
      totalDealValue += getDiscountValue(deal, numberOfTires, priceBeforeDeals);
    }

    // console.log(`totalDealValue: ${totalDealValue}`);
  });

  return totalDealValue;
};

export const generateSubtotals = ({
  frontTire, rearTire, pricingGroup, products, state, quantities,
}) => {
  let excludeProductsFromSubTotal = false;
  let subtotal = frontTire.productDetails.price[pricingGroup] * quantities.qtyFront;
  let qty = quantities.qtyFront;

  if (rearTire != null) {
    subtotal += rearTire.productDetails.price[pricingGroup] * quantities.qtyRear;
    qty += quantities.qtyRear;
  }

  if (frontTire?.deals?.length) {
    excludeProductsFromSubTotal = !!frontTire.deals.filter((deal) => deal.discount.type === 'instant' && deal.discount.amountType === 'percent').length;
  }

  // Sub total with products excluded if a instant percent deal is present
  let subtotalExcluded = subtotal;
  products.forEach((product) => {
    if (excludeProductsFromSubTotal === false) {
      subtotalExcluded += product.unitPrice * product.qty;
    }
    subtotal += product.unitPrice * product.qty;
  });

  const savings = getTotalDealValue(frontTire.deals, 'instant', qty, subtotalExcluded);
  // Rebates should be determined based on total value of cart after instant savings applied
  //  (true if rebate requires TDCC, may need confirmed for all other scenarios)
  const subtotalAfterSavings = subtotal - savings;
  const rebates = getTotalDealValue(frontTire.deals, 'rebate', qty, subtotalAfterSavings);

  return {
    subtotal,
    tireFeeState: state,
    tireFee: (stateFees[state] ?? 0) * qty,
    disposalFee: stateFees.disposal * qty,
    savings: savings * -1,
    rebates,
  };
};

export const totalPrice = ({
  subtotal, tireFee, disposalFee, savings,
}) => subtotal + tireFee + disposalFee + savings;

const storeHours = ({ openIntervals, isClosed }) => {
  if (isClosed) {
    return 'Closed';
  }

  const { start, end } = openIntervals[0];

  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth();
  const day = now.getDate();

  const [opensAtHour, opensAtMinute] = start.split(':');
  const [closesAtHour, closesAtMinute] = end.split(':');
  const opensAt = new Date(year, month, day, opensAtHour, opensAtMinute).toLocaleTimeString('en-US', { timeStyle: 'short' }).replace(' ', '');
  const closesAt = new Date(year, month, day, closesAtHour, closesAtMinute).toLocaleTimeString('en-US', { timeStyle: 'short' }).replace(' ', '');

  return `${opensAt} - ${closesAt}`;
};

export const storeInformation = ({
  number, name, address, phoneVisual, hours, lat, lng,
}) => {
  const {
    line1, city, region, postalCode,
  } = address;

  return {
    id: Number.parseInt(number, 10),
    number: Number.parseInt(number, 10),
    name,
    search_url: `//www.google.com/maps/search/${line1}+${city}+${region}+${postalCode}`.replace(/\s+/g, '+'),
    address: `${line1}, ${city}, ${region} ${postalCode}`,
    phone: phoneVisual,
    hours: `${storeHours(hours.monday)}*${storeHours(hours.saturday)}*${storeHours(hours.sunday)}`,
    addressData: {
      address: line1,
      city,
      state: region,
      zip: postalCode,
      latitude: lat,
      longitude: lng,
    },
  };
};

export const createCartObject = ({
  product, product2, quantities, installOption, addons,
}) => {
  const date = new Date();
  const { pricingGroup, state } = window.drupalSettings.storeDetails;
  let vehicle = window.drupalSettings.vehicleInformation || null;
  if (vehicle === null || vehicle.make === null) {
    vehicle = null;
  }

  let qty = quantities.qtyFront;
  if (product2 != null) {
    qty += quantities.qtyRear;
  }

  const products = generateProductsList(installOption, addons, qty);

  const subtotals = generateSubtotals({
    frontTire: product,
    rearTire: product2,
    pricingGroup,
    products,
    state,
    quantities,
  });

  const total = totalPrice(subtotals);

  const store = storeInformation(window.drupalSettings.storeDetails);

  // const { subtotal, tireFee, disposalFee } = subtotals;
  // const subTotalPlusFees = formatCurrency(subtotal + tireFee + disposalFee);
  // const validDeals = getEligibleDeals(product.deals, null, qty, subTotalPlusFees);

  return {
    hash: generateUUID(),
    uri: window.location.pathname,
    s: `${date.getFullYear()}-${date.getMonth() + 1}-${date.getDate()}`,
    qty,
    tire_front: {
      tire_id: product?.id,
      tire_manufacturer: product?.brand.name,
      tire_name: product?.modelName,
      tire_size: product?.productDetails.size,
      tire_sku: product?.primaryManufacturer?.sku,
      image: product?.images?.[0]?.url,
      price_per_tire: formatCurrency(product?.productDetails.price[pricingGroup]),
      qty: quantities.qtyFront,
      price_per_qty: formatCurrency(
        (product?.productDetails.price[pricingGroup] ?? 0) * quantities.qtyFront,
      ),
    },
    tire_rear: {
      tire_id: product2?.id ?? null,
      tire_manufacturer: product2?.brand.name ?? '',
      tire_name: product2?.modelName ?? '',
      tire_size: product2?.productDetails.size ?? '',
      tire_sku: product2?.primaryManufacturer?.sku,
      image: product2?.images?.[0]?.url ?? null,
      price_per_tire: product2 ? formatCurrency(product2?.productDetails.price[pricingGroup]) : '',
      qty: product2 ? quantities.qtyRear : null,
      price_per_qty: product2 ? formatCurrency(
        (product2?.productDetails.price[pricingGroup] ?? 0) * quantities.qtyRear,
      ) : '',
    },
    products,
    specials: product.deals?.map((deal) => ({
      id: deal.id,
      url: `${window.location.origin}/deals/${deal.slug}`,
      name: deal.marketing_summary,
      enddate: deal.end_date,
      classification: deal.type,
      code: '',
      quote_copy: deal.marketing_description,
    })) ?? [],
    subtotals: {
      subtotal: formatCurrency(subtotals.subtotal),
      tire_fee_state: subtotals.tireFeeState,
      tire_fee: formatCurrency(subtotals.tireFee),
      disposal_fee: formatCurrency(subtotals.disposalFee),
      savings: `-${formatCurrency(subtotals.savings)}`,
    },
    totals: {
      'totals-title': 'Estimated Total',
      total: formatCurrency(total),
      savings: formatCurrency(subtotals.savings),
    },
    store,
    vehicle,
  };
};

export const getCheckoutUrl = async (cartObject) => {
  const { url: cartUrl } = resolveService('createCart');
  const { url: checkoutUrl } = resolveService('checkout');
  const response = await fetch(cartUrl, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(cartObject),
  });

  if (!response.ok) {
    throw new Error('We were unable to generate a valid checkout URL.');
  }

  const checkoutObject = await response.json();
  return `${checkoutUrl}?data=${JSON.stringify(checkoutObject)}`;
};
